import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/ui';
import { getCockpitData, USE_MOCK_COCKPIT_DATA } from './data';
import { CockpitData, InvoiceRecord } from './types';
import { ExecBrief } from './components/ExecBrief';
import { KpiTiles, BLIND_FILTER, ALL_FILTER, TileFilter } from './components/KpiTiles';
import { InvoiceTable } from './components/InvoiceTable';
import { Dossier } from './components/Dossier';
import { LABEL_MAP, fmtDate } from './constants';

/**
 * Cashflow Cockpit — native React port of the payment-reconciliation report.
 *
 * Data flows from getCockpitData() (placeholder today, real API later — see ./data.ts).
 * Tiles filter the table by label; the blind-spot tile swaps in the silent-invoice set.
 * Clicking a row opens the dossier drawer.
 */
export const CashflowCockpit: React.FC = () => {
	const [data, setData] = useState<CockpitData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<TileFilter>(null);
	const [open, setOpen] = useState<InvoiceRecord | null>(null);

	useEffect(() => {
		let active = true;
		getCockpitData()
			.then(d => active && setData(d))
			.catch(e => active && setError(e?.message || 'Failed to load cockpit data'))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, []);

	/** Invoices after applying the active tile filter. */
	const filtered = useMemo<InvoiceRecord[]>(() => {
		if (!data) return [];
		// Blind-spot tile: show silent invoices (unpaid in Zoho, zero email trail).
		if (filter === BLIND_FILTER) {
			return (data.silent || []).map(s => ({
				num: s.num,
				cust: s.cust,
				amt: s.amt,
				labels: ['silent_invoice'],
				match: 'ai_unconfirmed',
				ostate: 'never_reached_out',
				z: s.z,
				c: { emails: 0, threads: 0 },
			}));
		}
		if (!filter || filter === ALL_FILTER) return data.invoices;
		return data.invoices.filter(r => r.labels.includes(filter));
	}, [data, filter]);

	const filterLabel =
		filter && filter !== ALL_FILTER
			? filter === BLIND_FILTER
				? 'blind spots / silent'
				: LABEL_MAP[filter]?.[0] || filter
			: null;

	return (
		<div className='flex h-full flex-col bg-slate-50/60 p-6'>
			<PageHeader title='Cashflow Cockpit' totalItems={0} itemType='analysis' icon='💸' />

			{data && (
				<div className='-mt-3 mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
					<span className='font-semibold tabular-nums text-slate-700'>
						{data.summary.n_invoice_records.toLocaleString('en-IN')}
					</span>
					<span className='text-slate-400'>invoices</span>
					<span className='text-slate-300'>·</span>
					<span className='font-semibold tabular-nums text-slate-700'>
						{data.summary.n_finance_threads.toLocaleString('en-IN')}
					</span>
					<span className='text-slate-400'>finance threads</span>
					<span className='text-slate-300'>·</span>
					<span className='text-slate-400'>generated {fmtDate(data.summary.generated_at)}</span>
					{USE_MOCK_COCKPIT_DATA && (
						<span className='ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700'>
							placeholder data
						</span>
					)}
				</div>
			)}

			{loading ? (
				<div className='flex flex-1 flex-col items-center justify-center text-gray-400'>
					<Loader2 className='mb-3 h-8 w-8 animate-spin' />
					<p className='text-sm'>Loading reconciliation data…</p>
				</div>
			) : error ? (
				<div className='rounded-lg border border-red-200 bg-red-50 p-4'>
					<div className='font-medium text-red-800'>Could not load the cockpit</div>
					<div className='mt-1 text-sm text-red-700'>{error}</div>
				</div>
			) : data ? (
				<div className='space-y-6'>
					<ExecBrief summary={data.summary} />
					<KpiTiles summary={data.summary} active={filter} onSelect={setFilter} />

					{filterLabel && (
						<div className='flex items-center gap-2 text-sm'>
							<span className='font-medium text-gray-700'>
								Filtered: {filterLabel} · {filtered.length}
							</span>
							<button
								onClick={() => setFilter(null)}
								className='rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200'
							>
								clear ✕
							</button>
						</div>
					)}

					<InvoiceTable invoices={filtered} onOpen={setOpen} />
				</div>
			) : null}

			<Dossier invoice={open} onClose={() => setOpen(null)} />
		</div>
	);
};

export default CashflowCockpit;
